import { Button } from '@/components/Button';
import { InfiniteScrollList } from '@/components/InfiniteScrollList';
import { Modal } from '@/components/Modal';
import { SearchInput } from '@/components/SearchInput';
import { Text } from '@/components/Text';
import { useContactActions } from '@/hooks/useContactActions';
import { ContactCard } from '@/partials/dashboard/Contacts/ContactCard';
import { ExportContactsModal } from '@/partials/dashboard/Contacts/ExportContactsModal';
import { useAuthStore } from '@/stores/useAuthStore';
import useContactStore from '@/stores/useContactStore';
import { Contact } from '@/types/contact';
import { isPC } from '@/utils/device';
import { AnimatePresence, motion } from 'framer-motion';
import { t } from 'i18next';
import { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { FaSync } from 'react-icons/fa';
import { IoMdPersonAdd } from 'react-icons/io';
import { MdDelete, MdFileDownload } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';

// Animations variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.05, when: 'beforeChildren' },
  },
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: { type: 'spring' as const, stiffness: 300, damping: 24 },
  },
};

const fadeIn = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.5 } },
};

export const ListContacts = () => {
  const {
    contacts,
    loading,
    fetchContacts,
    filters,
    bulkDeleteContacts,
    deleteContact,
    setFilters,
  } = useContactStore();
  const { prepareForExport, contactToDelete, setContactToDelete } =
    useContactActions();
  const { user } = useAuthStore();
  const [selectedContacts, setSelectedContacts] = useState<Contact[]>([]);
  const navigate = useNavigate();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isBulkDeleteModalOpen, setIsBulkDeleteModalOpen] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);

  useEffect(() => {
    if (user?.id && filters.current_page === 1) {
      const perPage = isPC() ? 20 : 10;

      if (filters.per_page !== perPage) {
        setFilters({ per_page: perPage });
      }

      fetchContacts(user.id, {
        current_page: filters.current_page,
        per_page: perPage,
        sort_field: filters.sort_field,
        sort_order: filters.sort_order,
      });
    }
  }, [
    user?.id,
    fetchContacts,
    filters.current_page,
    filters.per_page,
    filters.sort_field,
    filters.sort_order,
    setFilters,
  ]);

  const fetchMoreData = async () => {
    if (
      loading ||
      (filters.current_page || 1) >= (filters.last_page || 1) ||
      !user?.id
    ) {
      return;
    }

    const nextPage = (filters.current_page || 1) + 1;
    await fetchContacts(user.id, {
      ...filters,
      current_page: nextPage,
    });
  };

  const handleSelectContact = (contact: Contact) => {
    setSelectedContacts(prevSelected =>
      prevSelected.find(c => c.id === contact.id)
        ? prevSelected.filter(c => c.id !== contact.id)
        : [...prevSelected, contact],
    );
  };

  const handleBulkAction = (action: 'delete' | 'export') => {
    switch (action) {
      case 'delete':
        setIsBulkDeleteModalOpen(true);
        break;
      case 'export':
        openExportModal(selectedContacts);
        break;
      default:
        break;
    }
  };

  const handleBulkDeleteConfirmed = async () => {
    if (!user?.id) return;

    const contactIds = selectedContacts.map(c => c.id);
    await bulkDeleteContacts(user.id, contactIds);
    setSelectedContacts([]);
    setIsBulkDeleteModalOpen(false);
  };

  const openDeleteModal = (contact: Contact) => {
    setContactToDelete(contact);
    setIsDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setContactToDelete(null);
  };

  const handleDeleteConfirm = async () => {
    if (!contactToDelete || !user?.id) return;

    await deleteContact(user.id, contactToDelete.id);
    closeDeleteModal();
  };

  const closeBulkDeleteModal = () => {
    setIsBulkDeleteModalOpen(false);
  };

  const openExportModal = (contacts: Contact[]) => {
    if (contacts.length > 0) {
      prepareForExport(contacts);
      setIsExportModalOpen(true);
    } else {
      toast.error(t('listContacts:export.error.noContacts'));
    }
  };

  const closeExportModal = () => {
    setIsExportModalOpen(false);
    handleResetSelection();
  };

  const handleResetSelection = () => {
    setSelectedContacts([]);
  };

  const handleEdit = (contact: Contact) => {
    navigate(`${contact.id}`);
  };

  const handleExport = (contact: Contact) => {
    openExportModal([contact]);
  };

  const handleSearch = async (value: string) => {
    if (user?.id) {
      await fetchContacts(user.id, {
        ...filters,
        current_page: 1,
        search: value,
      });
    }
  };

  const handleRefresh = async () => {
    if (user?.id) {
      await fetchContacts(user.id, {
        ...filters,
        current_page: 1,
      });
    }
  };

  return (
    <div id="scrollableDiv" className="h-full space-y-6 overflow-y-auto">
      <motion.div
        initial="hidden"
        animate="visible"
        variants={fadeIn}
        className="space-y-6 px-4 pt-4"
      >
        {/* Header */}
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-2">
            <h1 className="text-lg font-semibold text-secondary">
              {t('listContacts:title')}
            </h1>
            <Text className="text-sm font-light text-gray-light">
              ({contacts.length})
            </Text>
            <button
              onClick={handleRefresh}
              className="ml-1 rounded p-1 hover:bg-gray-100"
            >
              <FaSync className="size-3 text-gray-light" />
            </button>
          </div>
          <Button
            onClick={() => navigate('create')}
            className="flex w-full items-center justify-center gap-2 lg:w-auto"
          >
            <IoMdPersonAdd className="size-4" />
            {t('listContacts:create-contact')}
          </Button>
        </div>

        {/* Barre de recherche */}
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeIn}
          className="max-w-md"
        >
          <SearchInput onChange={handleSearch} value={filters.search || ''} />
        </motion.div>

        {/* Actions en masse */}
        {selectedContacts.length > 0 && (
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeIn}
            className="flex gap-2"
          >
            <Button
              onClick={() => handleBulkAction('export')}
              className="flex-1 bg-gray-100 text-gray-dark hover:bg-gray-200"
            >
              <MdFileDownload className="size-5" />
              {t('common:export')} ({selectedContacts.length})
            </Button>
            <Button
              onClick={() => handleBulkAction('delete')}
              className="flex-1 bg-red-500 text-white hover:bg-red-600"
            >
              <MdDelete className="size-5" />
              {t('common:delete')} ({selectedContacts.length})
            </Button>
          </motion.div>
        )}
      </motion.div>

      {/* Liste des contacts avec InfiniteScrollList */}
      {contacts.length === 0 && !loading ? (
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeIn}
          className="flex flex-col items-center justify-center px-4 pb-4"
        >
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="mb-4 flex size-20 items-center justify-center rounded-full bg-gray-100">
              <IoMdPersonAdd className="size-10 text-gray-400" />
            </div>
            <h3 className="mb-2 text-lg font-semibold text-gray-900">
              {t('listContacts:empty.title')}
            </h3>
            <p className="mb-6 max-w-sm text-sm text-gray-500">
              {t('listContacts:empty.description')}
            </p>
            <Button
              onClick={() => navigate('create')}
              className="bg-linko-light hover:bg-linko-dark"
            >
              {t('listContacts:create-contact')}
            </Button>
          </div>
        </motion.div>
      ) : (
        <InfiniteScrollList
          dataLength={contacts.length}
          next={fetchMoreData}
          hasMore={(filters.current_page || 1) < (filters.last_page || 1)}
          total={contacts.length}
          scrollableTarget="scrollableDiv"
        >
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="px-4"
          >
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
              <AnimatePresence>
                {contacts.map(contact => (
                  <motion.div
                    key={contact.id}
                    variants={itemVariants}
                    className="h-48"
                  >
                    <ContactCard
                      contact={contact}
                      onDelete={openDeleteModal}
                      onEdit={handleEdit}
                      onExport={handleExport}
                      onSelect={handleSelectContact}
                      isSelected={selectedContacts.some(
                        c => c.id === contact.id,
                      )}
                    />
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          </motion.div>
        </InfiniteScrollList>
      )}

      {/* Modals */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={closeDeleteModal}
        className="w-auto"
        title={t('listContacts:delete.validation.title')}
      >
        <p className="mb-4">
          {t('listContacts:delete.validation.description', {
            name: contactToDelete?.full_name,
          })}
        </p>
        <div className="mt-4 flex flex-col space-y-3 sm:flex-row sm:justify-end sm:space-x-3 sm:space-y-0">
          <Button onClick={closeDeleteModal}>{t('common:cancel')}</Button>
          <Button className="bg-danger" onClick={handleDeleteConfirm}>
            {t('common:confirm')}
          </Button>
        </div>
      </Modal>

      <Modal
        isOpen={isBulkDeleteModalOpen}
        onClose={closeBulkDeleteModal}
        className="w-auto"
        title={t('listContacts:bulkDelete.validation.title')}
      >
        <p className="mb-4">
          {t('listContacts:bulkDelete.validation.description', {
            count: selectedContacts.length,
          })}
        </p>
        <div className="mt-4 flex flex-col space-y-3 sm:flex-row sm:justify-end sm:space-x-3 sm:space-y-0">
          <Button onClick={closeBulkDeleteModal}>{t('common:cancel')}</Button>
          <Button className="bg-danger" onClick={handleBulkDeleteConfirmed}>
            {t('common:confirm')}
          </Button>
        </div>
      </Modal>

      <ExportContactsModal
        isOpen={isExportModalOpen}
        onClose={closeExportModal}
        selectedContacts={selectedContacts}
        onExportComplete={handleResetSelection}
      />
    </div>
  );
};
